// GOOGLE-ADS-LANDING-PAGES-001 (revision 3) — official state license lookups
// for mental health professionals, used by the state picker on
// /legitimate-esa-letter and the sample-letter panels on the state apartment
// pages.
//
// Every link is a state licensing board's or state agency's own lookup — no
// third-party aggregators. Where a board runs its search on a vendor domain
// (Salesforce, Thentia, mylicense, Accela …) the link is the one the board's
// official site points to; NON_GOV_HOSTS in scripts/check-ads-landing-pages.mjs
// lists each such host. Each URL was loaded and its content checked on
// LICENSE_LOOKUPS_CHECKED (from US test servers where a state blocks non-US
// traffic). `covers` lists only license types the lookup was confirmed to include.

import type { LicenseLookupLink } from "./SampleLetterChecklistPanel";

export const LICENSE_LOOKUPS_CHECKED = "2026-10-05";

export type Profession = "psychologists" | "social workers" | "counselors" | "marriage and family therapists";

export interface LicenseLookupEntry extends LicenseLookupLink {
  covers: Profession[];
}

export interface StateLicenseLookups {
  code: string;
  name: string;
  links: LicenseLookupEntry[];
  /** Shown under the links, e.g. when a board has no online search. */
  note?: string;
}

const P: Profession = "psychologists";
const SW: Profession = "social workers";
const C: Profession = "counselors";
const M: Profession = "marriage and family therapists";
const ALL: Profession[] = [P, SW, C, M];

export const NPI_REGISTRY_URL = "https://npiregistry.cms.hhs.gov/";

/** NCSBN's national nurse-license lookup (boards of nursing that participate). */
export const NURSE_LICENSE_LOOKUP: LicenseLookupLink = {
  label: "Nursys QuickConfirm",
  href: "https://www.nursys.com/LQC/LQCSearch.aspx",
};

export const STATE_LICENSE_LOOKUPS: StateLicenseLookups[] = [
  {
    code: "AL",
    name: "Alabama",
    links: [
      { label: "Alabama Board of Examiners in Psychology — license verification", href: "https://apps.psychology.alabama.gov/public/licensee.aspx", covers: [P] },
      { label: "Alabama State Board of Social Work Examiners — license search", href: "https://apps.socialwork.alabama.gov/shared/search.aspx", covers: [SW] },
      { label: "Alabama Board of Examiners in Counseling — licensee search", href: "https://abec.alabama.gov/licensees/licensee-search/", covers: [C] },
      { label: "Alabama Board of Examiners in Marriage and Family Therapy — license roster", href: "https://mft.alabama.gov/search.aspx", covers: [M] },
    ],
  },
  { code: "AK", name: "Alaska", links: [{ label: "Alaska Professional Licensing — license search", href: "https://www.commerce.alaska.gov/cbp/main/search/professional", covers: ALL }] },
  {
    code: "AZ",
    name: "Arizona",
    links: [
      { label: "Arizona Board of Psychologist Examiners — licensee search", href: "https://psychboard.az.gov/directory-licensee-search", covers: [P] },
      { label: "Arizona Board of Behavioral Health Examiners — directory and verification", href: "https://bbhe.az.gov/directory-and-verifications-search", covers: [SW, C, M] },
    ],
  },
  {
    code: "AR",
    name: "Arkansas",
    links: [
      { label: "Arkansas Psychology Board — license search and verification", href: "https://psychologyboard.arkansas.gov/", covers: [P] },
      { label: "Arkansas Social Work Licensing Board — roster search", href: "https://healthy.arkansas.gov/boards-commissions/boards/social-work-licensing-board/social-work-licensing-board-roster-search/", covers: [SW] },
      { label: "Arkansas Board of Examiners in Counseling — license search and verification", href: "https://healthy.arkansas.gov/boards-commissions/boards/examiners-in-counseling-arkansas-state-board/", covers: [C, M] },
    ],
  },
  { code: "CA", name: "California", links: [{ label: "DCA License Search", href: "https://search.dca.ca.gov/", covers: ALL }] },
  { code: "CO", name: "Colorado", links: [{ label: "Colorado DORA license lookup", href: "https://apps2.colorado.gov/dora/licensing/Lookup/LicenseLookup.aspx", covers: ALL }] },
  { code: "CT", name: "Connecticut", links: [{ label: "Connecticut eLicense license lookup", href: "https://www.elicense.ct.gov/Lookup/LicenseLookup.aspx", covers: ALL }] },
  { code: "DE", name: "Delaware", links: [{ label: "Delaware DELPROS license verification", href: "https://delpros.delaware.gov/OH_VerifyLicense", covers: ALL }] },
  { code: "DC", name: "District of Columbia", links: [{ label: "DC Health license verification (links to the lookup portal)", href: "https://dchealth.dc.gov/service/get-verification-your-license", covers: ALL }] },
  { code: "FL", name: "Florida", links: [{ label: "Florida Department of Health license verification", href: "https://mqa-internet.doh.state.fl.us/MQASearchServices/HealthCareProviders", covers: ALL }] },
  { code: "GA", name: "Georgia", links: [{ label: "Georgia Secretary of State licensee search", href: "https://goals.sos.ga.gov/GASOSOneStop/s/licensee-search", covers: ALL }] },
  { code: "HI", name: "Hawaii", links: [{ label: "Hawaii DCCA professional license search", href: "https://mypvl.dcca.hawaii.gov/public-license-search/", covers: ALL }] },
  { code: "ID", name: "Idaho", links: [{ label: "Idaho DOPL public license search", href: "https://edopl.idaho.gov/OnlineServices/?link=PubSearch", covers: ALL }] },
  { code: "IL", name: "Illinois", links: [{ label: "Illinois IDFPR — check a license", href: "https://idfpr.illinois.gov/checklicense.html", covers: ALL }] },
  { code: "IN", name: "Indiana", links: [{ label: "Indiana Professional Licensing Agency — search and verify a license", href: "https://mylicense.in.gov/EVerification/Search.aspx", covers: ALL }] },
  { code: "IA", name: "Iowa", links: [{ label: "Iowa DIAL professional license search", href: "https://amanda-portal.idph.state.ia.us/ibpl/portal/#/license/license-query", covers: ALL }] },
  { code: "KS", name: "Kansas", links: [{ label: "Kansas Behavioral Sciences Regulatory Board — license verification", href: "https://licensing.ks.gov/Verification_BSRB/Search.aspx", covers: ALL }] },
  {
    code: "KY",
    name: "Kentucky",
    links: [
      { label: "Kentucky Office of Occupations and Professions — license search", href: "https://oop.ky.gov/lic_search.aspx", covers: [P, C, M] },
      { label: "Kentucky Board of Social Work — license verification", href: "https://secure.kentucky.gov/formservices/BSW/LicenseVerification/search", covers: [SW] },
    ],
  },
  {
    code: "LA",
    name: "Louisiana",
    links: [
      { label: "Louisiana State Board of Examiners of Psychologists — licensee search", href: "https://www.lsbepportal.com/licensee-search", covers: [P] },
      { label: "Louisiana State Board of Social Work Examiners — licensee search", href: "https://www.labswe.org/index.cfm/licensee-search", covers: [SW] },
      { label: "Louisiana LPC Board of Examiners — licensee search", href: "https://www.lpcboard.org/licensee-search", covers: [C, M] },
    ],
  },
  { code: "ME", name: "Maine", links: [{ label: "Maine Office of Professional and Occupational Regulation — license search", href: "https://www.pfr.maine.gov/ALMSOnline/ALMSQuery/SearchIndividual.aspx", covers: ALL }] },
  {
    code: "MD",
    name: "Maryland",
    links: [
      { label: "Maryland Board of Examiners of Psychologists — license verification", href: "https://mdbnc.health.maryland.gov/psychverification/default.aspx", covers: [P] },
      { label: "Maryland Board of Social Work Examiners — license verification", href: "https://mdbnc.health.maryland.gov/bsweverification/default.aspx", covers: [SW] },
      { label: "Maryland Board of Professional Counselors and Therapists — license verification", href: "https://mdbnc.health.maryland.gov/pctverification/default.aspx", covers: [C, M] },
    ],
  },
  { code: "MA", name: "Massachusetts", links: [{ label: "Massachusetts — Check a Health Care License", href: "https://checkahealthlicense.mass.gov/", covers: ALL }] },
  { code: "MI", name: "Michigan", links: [{ label: "Michigan LARA — verify a license", href: "https://aca-prod.accela.com/MILARA/GeneralProperty/PropertyLookUp.aspx?isLicensee=Y&TabName=APO", covers: ALL }] },
  {
    code: "MN",
    name: "Minnesota",
    links: [
      { label: "Minnesota Board of Psychology — license lookup", href: "https://mnitservices.my.site.com/license/SearchBox", covers: [P] },
      { label: "Minnesota Board of Social Work — license lookup", href: "https://soc.hlb.state.mn.us/#/services/onlineLicenseSearch", covers: [SW] },
      { label: "Minnesota Board of Behavioral Health and Therapy — license verification", href: "https://bht.hlb.state.mn.us/#/onlineEntitySearch", covers: [C] },
      { label: "Minnesota Board of Marriage and Family Therapy — licensee lookup", href: "https://mft.hlb.state.mn.us/#/services/onlineLicenseSearch", covers: [M] },
    ],
  },
  {
    code: "MS",
    name: "Mississippi",
    links: [
      { label: "Mississippi Board of Psychology — license search", href: "https://www.msbop.ms.gov/secure/licensesearch.asp", covers: [P] },
      { label: "Mississippi Board of Examiners for Social Workers and MFTs — license search", href: "https://www.swmft.webapps.ms.gov/publicview/publicindsearch.aspx", covers: [SW, M] },
      { label: "Mississippi Board of Examiners for Licensed Professional Counselors — license search", href: "https://www.lpc.ms.gov/secure/licensesearch.asp", covers: [C] },
    ],
  },
  { code: "MO", name: "Missouri", links: [{ label: "Missouri Division of Professional Registration — licensee search", href: "https://pr.mo.gov/licensee-search.asp", covers: ALL }] },
  { code: "MT", name: "Montana", links: [{ label: "Montana Department of Labor & Industry — licensee lookup", href: "https://ebizws.mt.gov/PUBLICPORTAL/searchform?mylist=licenses", covers: ALL }] },
  { code: "NE", name: "Nebraska", links: [{ label: "Nebraska DHHS license search", href: "https://www.nebraska.gov/LISSearch/search.cgi", covers: ALL }] },
  {
    code: "NV",
    name: "Nevada",
    links: [
      { label: "Nevada Board of Psychological Examiners — license look-up", href: "https://www.psyexam.nv.gov/licensing/nevada-license-look-up/", covers: [P] },
      { label: "Nevada Board of Examiners for Social Workers — verify a license", href: "https://services.socwork.nv.gov/verify/index.asp", covers: [SW] },
      { label: "Nevada Board of Examiners for MFTs and Clinical Professional Counselors — public registry", href: "https://nvboe.certemy.com/public-registry/00b35480-36a9-4898-a052-c13871cce91e", covers: [C, M] },
    ],
  },
  { code: "NH", name: "New Hampshire", links: [{ label: "New Hampshire OPLC license lookup", href: "https://forms.nh.gov/licenseverification/", covers: ALL }] },
  { code: "NJ", name: "New Jersey", links: [{ label: "New Jersey Division of Consumer Affairs — license verification", href: "https://newjersey.mylicense.com/verification/", covers: ALL }] },
  { code: "NM", name: "New Mexico", links: [{ label: "New Mexico Regulation and Licensing Department — license search", href: "https://nmrldlpi.my.site.com/bcd/s/rld-public-search", covers: ALL }] },
  { code: "NY", name: "New York", links: [{ label: "NYSED Office of the Professions verification search", href: "https://www.op.nysed.gov/verification-search", covers: ALL }] },
  {
    code: "NC",
    name: "North Carolina",
    links: [
      { label: "North Carolina Psychology Board — license verification", href: "https://www.ncpsychologyboard.org/license-verification/", covers: [P] },
      { label: "NC Social Work Certification and Licensure Board — license lookup", href: "https://ncswb.igovsolution.net/online/Lookups/Individual_Lookup.aspx", covers: [SW] },
      { label: "NC Board of Licensed Clinical Mental Health Counselors — license verification", href: "https://portal.ncblcmhc.org/verification/search.aspx", covers: [C] },
      { label: "NC Marriage and Family Therapy Licensure Board — verify a licensee", href: "https://www.ncbmft.org/licensure/verify-a-licensee", covers: [M] },
    ],
  },
  {
    code: "ND",
    name: "North Dakota",
    links: [
      { label: "ND State Board of Psychologist Examiners — verify a license", href: "https://www.ndsbpe.org/verify/index.asp", covers: [P] },
      { label: "ND Board of Social Work Examiners — online verification", href: "https://www.ndbswe.com/verify/search.asp", covers: [SW] },
    ],
    note: "North Dakota's counselor and marriage and family therapy boards don't offer an online license search — contact the board directly.",
  },
  { code: "OH", name: "Ohio", links: [{ label: "Ohio eLicense license look-up", href: "https://elicense.ohio.gov/oh_verifylicense", covers: ALL }] },
  {
    code: "OK",
    name: "Oklahoma",
    links: [
      { label: "Oklahoma State Board of Examiners of Psychologists — license verification", href: "https://oklahoma.gov/psychology/license-verifications.html", covers: [P] },
      { label: "Oklahoma State Board of Licensed Social Workers — licensee search", href: "https://oklahoma.gov/socialworkers/licensing-rules/licensee-search.html", covers: [SW] },
      { label: "Oklahoma State Board of Behavioral Health Licensure — license verification", href: "https://oklahoma.gov/behavioralhealth/license-verification.html", covers: [C, M] },
    ],
  },
  {
    code: "OR",
    name: "Oregon",
    links: [
      { label: "Oregon Board of Psychology — license search", href: "https://obop.us.thentiacloud.net/webs/obop/register/", covers: [P] },
      { label: "Oregon Board of Licensed Social Workers — license look-up", href: "https://blsw.us.thentiacloud.net/webs/blsw/register/", covers: [SW] },
      { label: "Oregon Board of Licensed Professional Counselors and Therapists — license search", href: "https://oblpct.us.thentiacloud.net/webs/oblpct/register/", covers: [C, M] },
    ],
  },
  { code: "PA", name: "Pennsylvania", links: [{ label: "Pennsylvania Licensing System (PALS) — verify a license", href: "https://www.pals.pa.gov/#!/page/search", covers: ALL }] },
  { code: "RI", name: "Rhode Island", links: [{ label: "Rhode Island Department of Health — license verification", href: "https://healthri.mylicense.com/verification/", covers: ALL }] },
  { code: "SC", name: "South Carolina", links: [{ label: "South Carolina LLR licensee lookup", href: "https://verify.llronline.com/LicLookup/LookupMain.aspx", covers: ALL }] },
  {
    code: "SD",
    name: "South Dakota",
    links: [
      { label: "SD Board of Examiners of Psychologists — online verification", href: "https://www.sdboards.org/dss/psych/verify/", covers: [P] },
      { label: "SD Board of Social Work Examiners — online verification", href: "https://www.sdboards.org/dss/socialwork/verify/", covers: [SW] },
      { label: "SD Board of Examiners for Counselors and MFTs — online verification", href: "https://www.sdboardofcounselors.org/verify/", covers: [C, M] },
    ],
  },
  { code: "TN", name: "Tennessee", links: [{ label: "Tennessee Department of Health license verification", href: "https://internet.health.tn.gov/Licensure/", covers: ALL }] },
  { code: "TX", name: "Texas", links: [{ label: "Texas Behavioral Health Executive Council license verification", href: "https://bhec.texas.gov/tbhec/verify-a-license/", covers: ALL }] },
  { code: "UT", name: "Utah", links: [{ label: "Utah Division of Professional Licensing — licensee lookup", href: "https://secure.utah.gov/llv/search/index.html", covers: ALL }] },
  { code: "VT", name: "Vermont", links: [{ label: "Vermont Office of Professional Regulation — find a professional", href: "https://sos.vermont.gov/opr/find-a-professional", covers: ALL }] },
  { code: "VA", name: "Virginia", links: [{ label: "Virginia Department of Health Professions license lookup", href: "https://dhp.virginiainteractive.org/Lookup/Index", covers: ALL }] },
  { code: "WA", name: "Washington", links: [{ label: "Washington State Department of Health — provider credential search", href: "https://doh.wa.gov/licenses-permits-and-certificates/provider-credential-or-facility-search", covers: ALL }] },
  {
    code: "WV",
    name: "West Virginia",
    links: [
      { label: "WV Board of Examiners of Psychologists — license search", href: "https://psychbd.wv.gov/license-info/license-search", covers: [P] },
      { label: "WV Board of Social Work — license verification", href: "https://www.wvsocialworkboard.org/Licensure/License-Verification", covers: [SW] },
      { label: "WV Board of Examiners in Counseling — counselor and therapist verification", href: "https://wvbec.org/counselor-and-therapist-license-verification/", covers: [C, M] },
    ],
  },
  { code: "WI", name: "Wisconsin", links: [{ label: "Wisconsin DSPS license lookup", href: "https://license.wi.gov/s/license-lookup", covers: ALL }] },
  {
    code: "WY",
    name: "Wyoming",
    links: [
      { label: "Wyoming Board of Psychology — license lookup", href: "https://psychology.wyo.gov/public/lookup", covers: [P] },
      { label: "Wyoming Mental Health Professions Licensing Board — license verification", href: "https://mentalhealth.wyo.gov/public/license-verification", covers: [SW, C, M] },
    ],
  },
];

const LABEL: Record<Profession, string> = {
  psychologists: "psychologists",
  "social workers": "clinical social workers",
  counselors: "professional counselors",
  "marriage and family therapists": "marriage and family therapists",
};

/** "psychologists, clinical social workers and professional counselors" */
export function professionList(covers: Profession[]): string {
  const words = covers.map((p) => LABEL[p]);
  return words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** The main official lookup for a state (first listed), if one is recorded. */
export function primaryLicenseLookup(code: string): LicenseLookupLink | undefined {
  const link = STATE_LICENSE_LOOKUPS.find((s) => s.code === code)?.links[0];
  return link ? { label: link.label, href: link.href } : undefined;
}
